import { Box, Chip, Tooltip, Typography, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid';
import ProgressCircle from 'components/ProgressCircle';
import { useState, useEffect } from 'react';
import { colorTheme } from 'theme';
import { useParams } from 'react-router-dom';
import { getCustomersTotalPot } from 'service/CustomerService';
import { CurrencyFormater } from 'components/helpers/commonFunctions/CurrencyFormater';
import { capitalizeWords } from 'utils/GeneralUtil';
import { strings } from 'i18n/Strings';

interface ITotalPot {
  unitDetails: {
    totatUnitPrice: number | string;
  } | null;
  otherPensionsTotal: number | string | null;
  totalEmployeeContribution: string | null;
  totalEmployerContribution: string | null;
  firstName?: string;
  lastName?: string;
  isAccountVested?: boolean;
}

interface TotalPotResponse {
  totatPotDetails: ITotalPot;
}

interface DashboardProps {
  setFullName: (name: string) => void;
}

const Dashboard = ({ setFullName }: DashboardProps): JSX.Element => {
  const theme = useTheme();
  const colors = colorTheme(theme.palette.mode);
  const { id } = useParams();
  const [customerTotalPot, setCustomerTotalPot] = useState<ITotalPot | null>(
    null,
  );
  const [showContributions, setShowContributions] = useState(false);

  const getTotslPotData = (): void => {
    getCustomersTotalPot(Number(id))
      .then((res) => {
        const response = res as TotalPotResponse;
        setCustomerTotalPot(response?.totatPotDetails);
        const firstName = String(response?.totatPotDetails?.firstName ?? '');
        const lastName = String(response?.totatPotDetails?.lastName ?? '');
        const fullName = capitalizeWords(`${firstName} ${lastName}`.trim());
        setFullName(fullName);
      })
      .catch((err: Error) => {
        console.error('Error getting data:', err);
      });
  };

  useEffect(() => {
    getTotslPotData();
  }, []);

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleCircleClick = () => {
    setShowContributions(!showContributions);
  };

  const zenpensionsTotal = Number(
    customerTotalPot?.unitDetails?.totatUnitPrice ?? 0,
  );

  const otherPensionsTotal = Number(customerTotalPot?.otherPensionsTotal ?? 0);

  const totalPot = zenpensionsTotal + otherPensionsTotal;
  const isAccountVested = Boolean(customerTotalPot?.isAccountVested);

  return (
    <Box>
      {/* ROW 1 */}
      <Grid
        container
        direction="row"
        justifyContent="center"
        columns={12}
        sx={{ display: 'flex', padding: 1, gap: 2 }}
      >
        <Grid item xs={12} display="flex" justifyContent="flex-end">
          {isAccountVested ? (
            <Tooltip
              title={strings.displayText.vestedAccountContributionBlocked}
              placement="left"
            >
              <Chip
                label={strings.displayText.vestedPrsa}
                color="warning"
                variant="outlined"
                sx={{ fontWeight: 600 }}
              />
            </Tooltip>
          ) : (
            <Chip
              label={strings.displayText.nonVestedPrsa}
              color="success"
              variant="outlined"
              sx={{ fontWeight: 600 }}
            />
          )}
        </Grid>
        <Grid
          item
          xs={6}
          sm={6}
          md={6}
          sx={{
            my: 1,
            p: 4,
            borderRadius: 3,
            boxShadow: 2,
            bgcolor: colors.rgbGreen[100],
          }}
        >
          <Typography variant="h5" fontWeight="600" color="white">
            Total Pot
          </Typography>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            mt="25px"
            onClick={handleCircleClick}
          >
            <ProgressCircle
              size={220}
              progress={0.75}
              value={Number(totalPot.toFixed(7))}
            />
          </Box>
          {showContributions && (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              mt="25px"
              bgcolor="white"
              p={2}
              borderRadius={3}
              boxShadow={2}
            >
              <Typography
                variant="h6"
                fontWeight="600"
                color={colors.greenAccent[200]}
              >
                Zen Pensions Pot: {CurrencyFormater(zenpensionsTotal)}
              </Typography>
              <Typography
                variant="h6"
                fontWeight="600"
                color={colors.greenAccent[200]}
              >
                Other Pensions: {CurrencyFormater(otherPensionsTotal)}
              </Typography>
              <Typography
                variant="h6"
                fontWeight="600"
                color={colors.greenAccent[200]}
              >
                Total Pot: {CurrencyFormater(totalPot)}
              </Typography>
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
